import axios from 'axios'
import { AuthInterceptor, UnauthorizedInterceptor } from '@/services/ApiInterceptors'

function dataMethod (method, args) {
  return this[method](...args).then(response => response.data.data)
}

export class Api {
  constructor (service = null) {
    this.service = service || axios

    this.service.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
    this.service.defaults.headers.common['Accept'] = 'application/json'
    this.service.defaults.headers.common['Content-Type'] = 'application/json'
    this.service.defaults.baseURL = process.env.VUE_APP_API
    this.service.interceptors.request.use(AuthInterceptor)
    this.service.interceptors.response.use(undefined, UnauthorizedInterceptor)
  }

  /**
   * Get request
   * @param args
   * @return {AxiosPromise<any>}
   */
  get (...args) {
    return this.service.get(...args)
  }

  getData (...args) { return dataMethod.call(this, 'get', args) }

  /**
   * Post request
   * @param args
   * @return {AxiosPromise<any>}
   */
  post (...args) {
    return this.service.post(...args)
  }

  postData (...args) { return dataMethod.call(this, 'post', args) }

  /**
   * Put request
   * @param args
   * @return {AxiosPromise<any>}
   */
  put (...args) {
    return this.service.put(...args)
  }

  putData (...args) { return dataMethod.call(this, 'put', args) }

  /**
   * Patch request
   * @param args
   * @return {AxiosPromise<any>}
   */
  patch (...args) {
    return this.service.patch(...args)
  }

  patchData (...args) { return dataMethod.call(this, 'patch', args) }

  /**
   * Delete request
   * @param args
   * @return {AxiosPromise}
   */
  delete (...args) {
    return this.service.delete(...args)
  }

  deleteData (...args) { return dataMethod.call(this, 'delete', args) }

  /**
   * Recursively fetches all items from a cursor paginated endpoint.
   *
   * @param {string} endpoint
   * @returns Promise
   */
  fetchAllWithCursor (endpoint) {
    return new Promise((resolve, reject) => {
      let items = []

      let cursor = null
      let previous = null

      const cb = (data, error) => {
        if (error) return reject(error)

        // We're done if we don't have more items
        if (!data.meta || !data.meta.cursor.next) {
          return resolve([...items, ...data.data])
        }

        // Shift cursors
        cursor = data.meta.cursor.next
        previous = data.meta.cursor.current

        // Append new data
        items = [...items, ...data.data]

        if (cursor !== null) {
          this.fetchWithCursor(endpoint, cursor, previous, cb)
        }
      }

      this.fetchWithCursor(endpoint, cursor, previous, cb)
    })
  }

  /**
   * Performs a GET request to fetch a chunk of data
   * using the provided cursor.
   *
   * @param {string} endpoint
   * @param {int|string} cursor
   * @param {int|string} previous
   * @param {function} callback
   */
  fetchWithCursor (endpoint, cursor, previous, callback) {
    this.get(endpoint, { params: { cursor, previous } })
      .then(response => callback(response.data, null))
      .catch(error => callback(null, error))
  }

  /**
   * Generates a cancel token
   * @return {CancelTokenSource}
   */
  cancelToken () {
    const CancelToken = this.service.CancelToken
    return CancelToken.source()
  }

  /**
   * Checks if a the param is a token
   * @return {boolean}
   * @param token
   */
  isCancelToken (token) {
    return this.service.isCancel(token)
  }
}

const api = new Api()

export default api
